/**
 * Shift pacing: what happens when the player clears a wave early.
 *
 * Measured before it was written, with a harness that kills every hostile two seconds
 * after it appears. A shift 3 run left 167 s of empty sea between the first fight and the
 * second; shift 6 left 322 s. That is the reward for shooting well, and it is backwards.
 *
 * Waves are gated two ways, and the dead air has a different cause in each:
 *
 *  - **`at` triggers** — an absolute time on the shift clock. Clear early and you simply
 *    wait for the number. Fixed by running that clock on while the sector is clear.
 *  - **`whenWithin` triggers** — a friendly reaching some range, which is what puts an
 *    ambush inside the gun's envelope. Here the wait is the ship's approach, and firing
 *    the beat early would drop it back outside that envelope: exactly the bug that once
 *    had skiffs attacking a tanker 2 km out. So the trigger is untouched and the *voyage*
 *    is compressed instead — the ambush still lands at precisely 2.8 km, it just stops
 *    taking five minutes of watching a hull crawl to get there.
 *
 * Neither touches `shift.elapsed`. The watchdog and the timer the player reads stay on
 * real time, so a shift never silently shortens; only the queue of arrivals moves up.
 */

/**
 * Seconds of genuine calm after the last hostile dies before the clock starts running on.
 *
 * The quiet beat after a fight is doing work — it is where a kill lands, where the sea
 * comes back, and the shift scripts explicitly bank on it ("gives the player a quiet beat
 * to absorb it before the next arrives"). Compressing from the instant the last hull goes
 * down would delete that, which is a different complaint than the one this fixes.
 */
export const LULL_GRACE_SECONDS = 6;

/**
 * How fast the trigger clock runs once the grace has passed. 5 means a 120 s gap cleared
 * at the 40 s mark closes in about 15 s of real time rather than 74.
 *
 * Deliberately a multiplier and not a skip-to-next: the gap still exists, still varies
 * with how early you cleared, and a wave still arrives out of quiet water rather than
 * teleporting in the moment the previous one sinks.
 */
export const LULL_RATE = 5;

/**
 * How close a friendly may be hauled by the lull's route compression, metres.
 *
 * `ships.ts` already compresses route progress ×4 beyond 6 km and drops to real time
 * inside 5 km, so a hull's last 2 km is deliberately honest. That last leg is most of the
 * dead air: at 7 m/s, 5 km down to a 2.8 km ambush is over five minutes of watching a
 * ship steam. During a lull the honest band shrinks to this instead.
 *
 * It sits below every ambush threshold the scripts use on purpose: by the time a wave is
 * due to trigger, the ship is close to her own speed, so nothing the player watches during
 * a fight is sped up. The guarantee is actually stronger than this number — a lull *ends*
 * the moment a hostile draws breath, which drops every hull to real time instantly — so
 * this is about how a ship looks while the sea is empty, not about fairness.
 *
 * Lowered 1500 -> 1000 once the campaign's final waves moved in to 1.3 km. Measured, the
 * old floor was the single biggest remaining hole: a tanker crossing from 2.6 km to her
 * last ambush gate ran the whole way at x1 on an empty ocean, seventy-six seconds of it in
 * shift 2 alone. Below the floor she is doing about x2 — brisk, not teleporting — and she
 * is back to x1 by the Safe Line whatever happens.
 */
export const LULL_ROUTE_NEAR_METRES = 1000;

/**
 * Peak route compression during a lull, against the plan's standing ×4.
 *
 * The opening probe fixed time-to-first-contact but exposed what was behind it: with the
 * shift's first fight now at 22 s, the convoy's run in from 9 km became a visible 180 s
 * hole rather than an invisible part of the opening. Same dead air, just relocated to
 * where it can be seen.
 *
 * Raising the ceiling only bites where the standing ramp already applies — outside 6 km,
 * falling away to real time by `LULL_ROUTE_NEAR_METRES`. So the hull is quick while it is
 * a radar contact and back to its own speed long before it is something the player can
 * look at, which is the same bargain plan E1 already struck, one notch further.
 *
 * Raised again, 9 -> 14, once the campaign's remaining gaps were measured and turned out
 * to share a single cause: the convoy's own run from where she spawns to where her first
 * ambush gate opens. Every shift had a fifty-to-ninety second hole in exactly that place,
 * and no amount of script authoring closes it, because it is not a scheduling problem —
 * it is three or four kilometres of ocean. At 5 km this puts a tanker at about x12, and
 * at 2 km at about x2.7; the ramp still lands at x1 by 1.5 km, so the fast part happens
 * where she is a mark on the radar and the honest part where she is a ship.
 */
export const LULL_ROUTE_MAX = 14;

/**
 * Peak route compression once the shift is *decided* — every beat fired and the sector
 * clear — measured against `LULL_ROUTE_MAX`'s 9.
 *
 * A lull is a pause the player earned; a decided shift is one that is simply over, and
 * the two deserve different treatment. While the script is live the compression floor
 * stays at `LULL_ROUTE_NEAR_METRES` so an ambush can never be dragged inside the gun's
 * envelope. Once no beat is left to fire, no ambush *can* be staged, so the floor drops
 * to the Safe Line itself and the last stretch closes at this rate instead.
 *
 * The ramp is what keeps it honest. It is anchored at `COMPRESSION_FAR_METRES` and lands
 * at ×1 on the line, so a hull is quick while she is a radar contact and back to her own
 * speed by the time she is something the player looks at: about ×1.9 at 1.2 km and ×2.7
 * at 1.5 km. A tanker visibly hustling next to the platform would be a worse defect than
 * the dead air this removes.
 */
export const DECIDED_ROUTE_MAX = 12;

/**
 * The beat between the last thing happening and the results screen, seconds.
 *
 * Ending on the tick a condition flips is how a simulation stops; it is not how a watch
 * ends. This is the breath in between — the last wreck still burning, the tanker sliding
 * in past it, one line from Rig Control — and it is the difference between a shift that
 * finishes and one that is merely switched off.
 */
export const SHIFT_OUTRO_SECONDS = 3;
